import { useCallback, useEffect, useState } from 'react';
import { Button, Textarea } from '../../../components/ui';
import Modal from '../../../components/ui/Modal';
import { useAppContext } from '../../../contexts/AppContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { startAdminImpersonation } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';
import { persistSupportSession } from '../hooks/useSupportSession';

const MIN_REASON_LENGTH = 20;

type ImpersonationTarget = {
    id: string;
    name: string;
};

type ImpersonationStartDialogProps = {
    open: boolean;
    org: ImpersonationTarget | null;
    onClose: () => void;
};

export function ImpersonationStartDialog({ open, org, onClose }: ImpersonationStartDialogProps) {
    const { setCurrentPage } = useAppContext();
    const { refreshOrganizations, setCurrentOrganization } = useOrganization();
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const reasonValid = reason.trim().length >= MIN_REASON_LENGTH;

    useEffect(() => {
        if (!open) {
            setReason('');
            setIsSubmitting(false);
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!org || !reasonValid || isSubmitting) return;

        setIsSubmitting(true);
        try {
            const result = await startAdminImpersonation(org.id, reason.trim());
            const orgs = await refreshOrganizations(true);
            const targetOrg = orgs.find((o) => o.id === org.id);

            if (!targetOrg) {
                throw new Error('Organização não encontrada após iniciar modo manutenção');
            }

            setCurrentOrganization(targetOrg);

            persistSupportSession({
                sessionId: result.sessionId,
                organizationId: result.organizationId,
                organizationName: org.name,
                expiresAt: result.expiresAt,
            });

            notify.success(`Modo manutenção iniciado em ${org.name}.`);
            onClose();
            setCurrentPage('dashboard');
        } catch (e) {
            notify.error(
                e instanceof SuperadminApiError
                    ? e.message
                    : e instanceof Error
                      ? e.message
                      : 'Falha ao iniciar modo manutenção',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={open} onClose={onClose} size="md" preventBackdropClose={isSubmitting}>
            <Modal.Header onClose={onClose} showCloseButton={!isSubmitting}>
                Entrar como empresa
            </Modal.Header>

            <Modal.Body>
                <div className="flex flex-col gap-s4">
                    <p className="text-sm text-secondary">
                        Você acessará a conta de{' '}
                        <span className="font-medium text-default">{org?.name ?? '—'}</span> em modo
                        manutenção (LGPD). Sua sessão expira em 60 minutos.
                    </p>

                    <Textarea
                        label="Motivo do acesso"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Descreva o motivo operacional (mínimo 20 caracteres)…"
                        minRows={3}
                        disabled={isSubmitting}
                        helperText={`${reason.trim().length}/${MIN_REASON_LENGTH} caracteres mínimos`}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                    Cancelar
                </Button>
                <Button
                    type="button"
                    onClick={() => void handleSubmit()}
                    disabled={!org || !reasonValid || isSubmitting}
                >
                    {isSubmitting ? 'Iniciando…' : 'Acessar conta'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
